import { TileDataset } from '../../shared/models/tile-dataset';
import {TileValue} from '@shared/models/tile-value';

/**
 * Navigation direction for timestamp adjustment.
 */
export enum NavigationDirection {
  OLDER = -1,
  NEWER = 1,
}

/**
 * Helper class for tile history navigation logic.
 * Centralizes timestamp handling, boundary adjustment, and navigation range calculations.
 * PLAT-277: Preserves microsecond precision (6 digits) and handles direction-aware truncation.
 */
export class TileHistoryNavigationHelper {

  /** Microseconds per second (used for overflow detection in microsecond adjustment) */
  private static readonly MICROSECONDS_PER_SECOND = 1_000_000;

  /**
   * Extracts the overall oldest and newest timestamps across all datasets.
   */
  static extractBoundaries(
    datasets: TileDataset[]
  ): { oldest: string | null; newest: string | null } {
    const oldest = datasets.reduce((min, ds) =>
      ds.oldestTimestampIso && (!min || ds.oldestTimestampIso < min) ? ds.oldestTimestampIso : min,
      null as string | null
    );
    const newest = datasets.reduce((max, ds) =>
      ds.newestTimestampIso && (!max || ds.newestTimestampIso > max) ? ds.newestTimestampIso : max,
      null as string | null
    );
    return { oldest, newest };
  }

  /**
   * Extracts oldest/newest timestamps from API response values.
   * @param ascending - Sort order: true = first is oldest, false/null = first is newest
   */
  static extractBoundariesFromValues(
    values: TileValue[],
    ascending: boolean | null
  ): { oldestTimestampIso: string | undefined; newestTimestampIso: string | undefined } {
    if (values.length === 0) {
      return { oldestTimestampIso: undefined, newestTimestampIso: undefined };
    }

    const result = ascending
      ? { oldestTimestampIso: values[0]?.Time, newestTimestampIso: values[values.length - 1]?.Time }
      : { newestTimestampIso: values[0]?.Time, oldestTimestampIso: values[values.length - 1]?.Time };

    return result;
  }

  /**
   * Adjusts a boundary timestamp to avoid duplicate data when navigating.
   * When truncated, returns exact boundary (more data exists at that point).
   * When not truncated, adjusts by ±1ns (ISO) or ±1ms (number) to skip the boundary.
   *
   * @param timestamp - Boundary timestamp (ISO string or milliseconds)
   * @param direction - NavigationDirection.NEWER or NavigationDirection.OLDER
   * @param wasTruncated - True if data was truncated in the same direction as navigation
   */
  static adjustBoundary(
    timestamp: string | number,
    direction: NavigationDirection,
    wasTruncated: boolean
  ): string | number {
    if (wasTruncated) {
      return timestamp;
    }

    if (typeof timestamp === 'string') {
      return this.adjustIsoByMicrosecond(timestamp, direction);
    } else {
      return timestamp + direction;
    }
  }

  /**
   * Adjusts an ISO 8601 timestamp by ±1 microsecond to preserve precision.
   * Falls back to ±1ms on overflow (e.g., .999999Z + 1μs).
   */
  static adjustIsoByMicrosecond(iso: string, direction: NavigationDirection): string {
    const match = iso.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})\.?(\d{0,9})(Z?)$/);

    if (!match) {
      console.warn('Invalid ISO format for microsecond adjustment:', iso);
      return iso;
    }

    const [_fullMatch, baseTime, fractionalStr, zulu] = match;
    // Limit to 6 digits (microseconds)
    const fractional = (fractionalStr || '0').substring(0, 6).padEnd(6, '0');
    let microseconds = parseInt(fractional, 10) + direction;

    if (microseconds >= this.MICROSECONDS_PER_SECOND || microseconds < 0) {
      // Handle overflow/underflow by adjusting the seconds
      const date = new Date(baseTime + 'Z');
      date.setUTCSeconds(date.getUTCSeconds() + (microseconds < 0 ? -1 : 1));
      const newBaseTime = date.toISOString().replace(/\.\d{3}Z$/, '');
      const newMicroseconds = microseconds < 0
        ? this.MICROSECONDS_PER_SECOND + microseconds
        : microseconds - this.MICROSECONDS_PER_SECOND;
      const newFractional = newMicroseconds.toString().padStart(6, '0');
      return `${newBaseTime}.${newFractional}Z`;
    }

    const newFractional = microseconds.toString().padStart(6, '0');
    return `${baseTime}.${newFractional}${zulu}`;
  }

  /**
   * Converts timestamp to milliseconds (handles both ISO strings and numbers).
   */
  static toMilliseconds(timestamp: string | number): number {
    return typeof timestamp === 'string'
      ? new Date(timestamp).getTime()
      : timestamp;
  }

  /**
   * Calculates the navigation range for a given boundary and seek length.
   * Returns both storage format (milliseconds) and API format (preserves ISO precision).
   *
   * @param boundary - Timestamp to navigate from (oldest for Older, newest for Newer)
   * @param seekLengthMs - Search window size (60 days LoRaWAN, 14 days BACnet/Modbus)
   * @param direction - NavigationDirection.OLDER or NavigationDirection.NEWER
   * @param wasTruncated - True if data was truncated in the same direction
   */
  static calculateNavigationRange(
    boundary: string | number,
    seekLengthMs: number,
    direction: NavigationDirection,
    wasTruncated: boolean
  ): { start: number; end: number; rangeStartForApi: number | string; rangeEndForApi: number | string } {
    const adjustedBoundary = this.adjustBoundary(boundary, direction, wasTruncated);

    if (direction === NavigationDirection.OLDER) {
      const rangeEndMs = this.toMilliseconds(adjustedBoundary);
      return {
        start: rangeEndMs - seekLengthMs,
        end: rangeEndMs,
        rangeStartForApi: rangeEndMs - seekLengthMs,
        rangeEndForApi: adjustedBoundary,
      };
    } else {
      const rangeStartMs = this.toMilliseconds(adjustedBoundary);
      return {
        start: rangeStartMs,
        end: rangeStartMs + seekLengthMs,
        rangeStartForApi: adjustedBoundary,
        rangeEndForApi: rangeStartMs + seekLengthMs,
      };
    }
  }
}
